import React, { useState } from 'react';
import {
  useSetStudentLegalHoldMutation,
  useClearStudentLegalHoldMutation,
} from '../../store/api/localApi';
import type { Student } from '../../types';

/**
 * Legal-hold set/clear panel on the student detail page (Task 21). Admin-only
 * (rendered inside a RoleGate by the caller). Shows the current held state +
 * reason and a "clocks frozen" note when held. A hold FREEZES the child's
 * retention clocks — it never deletes anything.
 */
const StudentLegalHoldPanel: React.FC<{ student: Student }> = ({ student }) => {
  const [setHold, { isLoading: isSetting }] = useSetStudentLegalHoldMutation();
  const [clearHold, { isLoading: isClearing }] = useClearStudentLegalHoldMutation();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const held = !!student.legalHold;

  const onSet = async () => {
    setError(null);
    if (reason.trim().length === 0) {
      setError('A reason is required to place a legal hold.');
      return;
    }
    try {
      await setHold({ studentId: student.id, reason: reason.trim() }).unwrap();
      setReason('');
    } catch {
      setError('Could not set the legal hold. Please try again.');
    }
  };

  const onClear = async () => {
    setError(null);
    try {
      await clearHold({ studentId: student.id }).unwrap();
    } catch {
      setError('Could not clear the legal hold. Please try again.');
    }
  };

  return (
    <div data-testid="student-legal-hold" className="mt-6 rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900">Legal hold</h3>

      {held ? (
        <div className="mt-2">
          <span
            data-testid="student-legal-hold-badge"
            className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800"
          >
            On legal hold
          </span>
          <p data-testid="student-legal-hold-reason-text" className="mt-2 text-sm text-gray-700">
            Reason: {student.legalHold?.reason}
          </p>
          <p data-testid="student-legal-hold-frozen-note" className="mt-1 text-xs text-amber-700">
            This child's retention clocks are frozen while the hold is in place. Nothing is deleted.
          </p>
          <button
            type="button"
            onClick={onClear}
            disabled={isClearing}
            data-testid="student-legal-hold-clear"
            className="mt-3 inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {isClearing ? 'Clearing…' : 'Clear legal hold'}
          </button>
        </div>
      ) : (
        <div className="mt-2">
          <p className="text-xs text-gray-500">
            Placing a legal hold freezes this child's retention clocks so their records are kept
            until the hold is cleared. It never deletes anything.
          </p>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason for the hold (e.g. Tusla enquiry, complaint, litigation)"
            data-testid="student-legal-hold-reason"
            className="mt-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={onSet}
            disabled={isSetting}
            data-testid="student-legal-hold-set"
            className="mt-2 inline-flex items-center rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {isSetting ? 'Setting…' : 'Set legal hold'}
          </button>
        </div>
      )}

      {error && (
        <p data-testid="student-legal-hold-error" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default StudentLegalHoldPanel;
